import { useCustomerDataQualityGate } from '@components/UserGroupDataQuality/useCustomerDataQualityCheckout';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { PaymentOption } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BookingUserNotificationDto } from 'noddi-types';
import { NoddiButton, NoddiDialog, NoddiSkeleton, noddiToast } from 'noddi-ui-web';
import {
  commonTrackingEvents,
  tracking,
  useAuthContext,
  useBookingAddress,
  useBookingCars,
  useBookingTimeWindow,
  useShouldFireRequestForLoggedInUser
} from 'noddi-web';
import { useEffect, useState } from 'react';
import { OwnStorageFacilityPickupDialog } from '../../../../components/BookingFlow/Common/OwnStorageFacilityPickupDialog';
import { StuddedTireSeasonDialog } from '../../../../components/BookingFlow/Common/StuddedTireSeasonDialog';
import { TireHotelLocationSuggestionDialog } from '../../../../components/BookingFlow/Common/TireHotelLocationSuggestionDialog';
import { NotificationSettingsDialog } from '../../../../components/Summary/NotificationSettingsDialog';
import type { BookingSmsOptIns } from '../../../../hooks/useBookingSmsOptIns';
import { useMobileBookingCustomer } from '../../../../hooks/useMobileBookingCustomer';
import type { InvoiceReferenceInput } from '../../../../utils/stationaryConfirm';
import { getConfirmCtaText, getInvoiceReferenceBlocker } from '../../../../utils/stationaryConfirm';
import { useCreateBooking } from '../helpers/useCreateBooking';

interface Props {
  hasAcceptedTermsAndConditions: boolean;
  hasTermsAndConditions: boolean;
  onTermsValidationError: (error: string | null) => void;
  paymentOption: PaymentOption;
  shouldPayForBooking: boolean;
  /** Invoice recipient when it differs from the customer. Omitted when the customer pays. */
  userGroupPayerId?: number | null;
  /** Group the booking is created on. A non-member company car uses that company. */
  bookingUserGroupId: number | null;
  /** Set only when checkout asks for a reference on a non-member invoice. */
  invoiceReference?: InvoiceReferenceInput;
  /** Person the organization booking is assigned to. Omitted, the booker. */
  bookingUserId?: number | null;
  smsOptIns: Pick<BookingSmsOptIns, 'userIds' | 'notificationsToOverride'>;
  /** False when the cart needs no customer visit: then there is no time window to require. */
  needsCustomerVisit: boolean;
}

export const ConfirmBooking = ({
  hasAcceptedTermsAndConditions,
  hasTermsAndConditions,
  onTermsValidationError,
  paymentOption,
  shouldPayForBooking,
  userGroupPayerId,
  bookingUserGroupId,
  invoiceReference,
  bookingUserId,
  smsOptIns,
  needsCustomerVisit
}: Props) => {
  const selectedAddress = useBookingAddress();
  const bookingTimeWindow = useBookingTimeWindow();
  const selectedCars = useBookingCars();
  const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState(false);
  const shouldFireRequest = useShouldFireRequestForLoggedInUser();

  const { userData, isLoggedIn } = useAuthContext();
  const { userGroupId, userId } = useMobileBookingCustomer();
  const { createBooking, tireHotelSuggestion, ownStorageFacilityPickup, studdedTireSeason, isPending } =
    useCreateBooking();

  // Summary is the last step before conversion — last funnel checkpoint.
  useEffect(() => {
    tracking.track(commonTrackingEvents.summaryReached, { paymentOption });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only: re-firing on a payment-option toggle double-counts the checkpoint
  }, []);

  const {
    data: userGroup,
    // use isLoading not isPending, as we do not want loading skeleton before user has logged in
    isLoading
  } = noddiAsync.useGet({
    type: URLKeys.getUserGroupForCustomer,
    input: { userGroupId: userGroupId! },
    queryConfig: {
      enabled: shouldFireRequest
    }
  });

  const bookingArgs = {
    paymentMethod: paymentOption,
    userGroupPayerId,
    bookingUserGroupId,
    invoiceReference: invoiceReference?.value.trim(),
    bookedForUserId: bookingUserId
  };

  const proceedToBooking = async () => {
    const invoiceReferenceBlocker = getInvoiceReferenceBlocker(invoiceReference);
    if (invoiceReferenceBlocker) {
      noddiToast.error(invoiceReferenceBlocker);
      return;
    }
    // The picker lists the signed-in group's members. A booking on a company
    // the customer does not belong to notifies that company's drivers instead.
    const booksOnOwnGroup = bookingUserGroupId == null || bookingUserGroupId === userGroupId;
    if (booksOnOwnGroup && userGroup && userGroup.members.length > 1) {
      setIsNotificationDialogOpen(true);
      return;
    }

    await createBooking({ ...bookingArgs, notificationsToOverride: smsOptIns.notificationsToOverride });
  };

  const dataQuality = useCustomerDataQualityGate();
  const willPay = shouldPayForBooking && paymentOption === PaymentOption.PAY_BEFORE;

  if (isLoading) {
    return <NoddiSkeleton variant='button' className='h-9 w-36' />;
  }

  const handleConfirmWithNotifications = async (notificationsToOverride: BookingUserNotificationDto[]) => {
    await createBooking({ ...bookingArgs, notificationsToOverride });
  };

  const handleConfirmClick = async () => {
    onTermsValidationError(null);

    if (hasTermsAndConditions && !hasAcceptedTermsAndConditions) {
      onTermsValidationError(t`Please accept our terms and conditions to proceed with your booking.`);
      return;
    }

    if (
      dataQuality.holdForDataQuality({
        willPay,
        onSaved: () => {
          void proceedToBooking();
        }
      }) !== 'clear'
    ) {
      return;
    }

    await proceedToBooking();
  };

  const buttonLabel = getConfirmCtaText(paymentOption, willPay);

  return (
    <>
      {tireHotelSuggestion && <TireHotelLocationSuggestionDialog {...tireHotelSuggestion} isPending={isPending} />}
      {ownStorageFacilityPickup && <OwnStorageFacilityPickupDialog {...ownStorageFacilityPickup} />}
      {studdedTireSeason && <StuddedTireSeasonDialog {...studdedTireSeason} isPending={isPending} />}
      <NoddiDialog
        open={isNotificationDialogOpen}
        onClose={() => setIsNotificationDialogOpen(false)}
        title={t`Who should receive booking updates?`}
        description={t`Choose who should receive SMS updates for this booking.`}
      >
        <NotificationSettingsDialog
          // We know userGroup is defined here because dialog is only opened when userGroup exists
          userGroup={userGroup}
          userGroupId={userGroupId}
          bookedForUserId={userId}
          preselectedUserIds={smsOptIns.userIds}
          isConfirming={isPending}
          confirmLabel={willPay ? <Trans>Confirm and pay</Trans> : undefined}
          // oxlint-disable-next-line typescript/no-misused-promises -- async onConfirm runs handleConfirmWithNotifications(); React discards the returned promise, so a rejection is unhandled unless the body catches
          onConfirm={handleConfirmWithNotifications}
          licensePlateNumbers={selectedCars?.map((car) => car.licensePlate.number)}
        />
      </NoddiDialog>

      <div className='flex w-full items-center justify-end gap-2'>
        <NoddiButton
          variant='success'
          disabled={
            !userGroupId ||
            !userData ||
            !selectedAddress ||
            (needsCustomerVisit && !bookingTimeWindow) ||
            !isLoggedIn ||
            dataQuality.isChecking
          }
          loading={isPending}
          // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleConfirmClick(); React discards the returned promise, so a rejection is unhandled unless the body catches
          onClick={handleConfirmClick}
        >
          {buttonLabel}
        </NoddiButton>
      </div>
    </>
  );
};
